import type { ReactNode } from "react";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

/**
 * Matches either a `[label](url)` markdown link or a bare `http(s)://` URL.
 * Markdown comes first in the alternation so the URL inside `(...)` is not
 * matched separately.
 */
const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>"']+)/g;

/** Trailing sentence punctuation is almost never part of a pasted URL. */
function trimUrlTail(url: string): { href: string; tail: string } {
  const match = /[.,;:!?)\]]+$/.exec(url);
  if (!match) return { href: url, tail: "" };
  return { href: url.slice(0, match.index), tail: match[0] };
}

const LINK_CLASS =
  "font-medium text-bridge-accent underline underline-offset-2 hover:text-bridge-accent/80 break-all";

export function looksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text.trim());
}

/**
 * Renders note text with `[label](url)` markdown links and bare `http(s)://`
 * URLs as clickable anchors that open in a new tab. Other text is left
 * unchanged (line breaks preserved by parent `whitespace-pre-wrap`).
 */
export function renderTextWithLinks(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(LINK_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    lastIndex = index + match[0].length;

    const bareUrl = match[3];
    if (bareUrl) {
      const { href, tail } = trimUrlTail(bareUrl);
      if (href) {
        parts.push(
          <a
            key={`url-link-${key++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASS}
          >
            {href}
          </a>,
        );
        if (tail) parts.push(tail);
        continue;
      }
      parts.push(match[0]);
      continue;
    }

    const label = match[1];
    const href = match[2].trim();
    if (href) {
      parts.push(
        <a
          key={`md-link-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={LINK_CLASS}
        >
          {label}
        </a>,
      );
    } else {
      parts.push(match[0]);
    }
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/** Renders file-note body as React nodes — no dangerouslySetInnerHTML. */
export function NoteBody({ text }: { text: string }) {
  if (looksLikeHtml(text)) {
    const cleanHtml = DOMPurify.sanitize(text, { ADD_ATTR: ["target", "rel"] });
    return (
      <div className="prose prose-sm max-w-none break-words">
        {parse(cleanHtml, {
          replace: (node) => {
            // Anchors coming from note HTML must open in a new tab too.
            if ("attribs" in node && node.name === "a" && node.attribs) {
              node.attribs.target = "_blank";
              node.attribs.rel = "noopener noreferrer";
            }
            return undefined;
          },
        })}
      </div>
    );
  }

  return (
    <div className="text-sm leading-relaxed text-bridge-fg whitespace-pre-wrap break-words">
      {renderTextWithLinks(text)}
    </div>
  );
}
